import { c as _c } from "react/compiler-runtime";
import { varAlpha, mergeClasses } from 'minimal-shared/utils';
import { styled } from '@mui/material/styles';
import ButtonBase from '@mui/material/ButtonBase';
import { newSearchParams } from 'src/routes/paths';
import { useTranslate } from 'src/locales';
import { Iconify } from 'src/components/iconify';
import { createNavItem, navItemStyles, navSectionClasses } from 'src/components/nav-section';

// ----------------------------------------------------------------------

export function NavItem(t0) {
  const $ = _c(44);
  let active;
  let className;
  let externalLink;
  let hasChild;
  let open;
  let other;
  let path;
  let ref;
  let subItem;
  let title;
  if ($[0] !== t0) {
    ({
      ref,
      title,
      path,
      open,
      active,
      subItem,
      hasChild,
      className,
      externalLink,
      ...other
    } = t0);
    $[0] = t0;
    $[1] = active;
    $[2] = className;
    $[3] = externalLink;
    $[4] = hasChild;
    $[5] = open;
    $[6] = other;
    $[7] = path;
    $[8] = ref;
    $[9] = subItem;
    $[10] = title;
  } else {
    active = $[1];
    className = $[2];
    externalLink = $[3];
    hasChild = $[4];
    open = $[5];
    other = $[6];
    path = $[7];
    ref = $[8];
    subItem = $[9];
    title = $[10];
  }
  const {
    t,
    currentLang
  } = useTranslate();
  const t1 = `${path}${newSearchParams({
    country: currentLang.countryName
  })}`;
  let t2;
  if ($[11] !== externalLink || $[12] !== hasChild || $[13] !== t1) {
    t2 = createNavItem({
      path: t1,
      hasChild,
      externalLink
    });
    $[11] = externalLink;
    $[12] = hasChild;
    $[13] = t1;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  const navItem = t2;
  const t3 = !subItem ? "rootItem" : "subItem";
  let t4;
  if ($[15] !== active || $[16] !== open || $[17] !== t3) {
    t4 = {
      open,
      active,
      variant: t3
    };
    $[15] = active;
    $[16] = open;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const ownerState = t4;
  let t5;
  if ($[19] !== t || $[20] !== title) {
    t5 = t(title);
    $[19] = t;
    $[20] = title;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const t6 = navItem.baseProps;
  let t7;
  if ($[22] !== active || $[23] !== className || $[24] !== open) {
    t7 = mergeClasses([navSectionClasses.item.root, className], {
      [navSectionClasses.state.open]: open,
      [navSectionClasses.state.active]: active
    });
    $[22] = active;
    $[23] = className;
    $[24] = open;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== t || $[27] !== title) {
    t8 = t(title);
    $[26] = t;
    $[27] = title;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== ownerState || $[30] !== t8) {
    t9 = <ItemTitle {...ownerState}>{t8}</ItemTitle>;
    $[29] = ownerState;
    $[30] = t8;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== hasChild || $[33] !== ownerState) {
    t10 = hasChild && <ItemArrow {...ownerState} icon="eva:arrow-ios-downward-fill" />;
    $[32] = hasChild;
    $[33] = ownerState;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== navItem.baseProps || $[36] !== other || $[37] !== ownerState || $[38] !== ref || $[39] !== t10 || $[40] !== t5 || $[41] !== t7 || $[42] !== t9) {
    t11 = <ItemRoot disableRipple={true} ref={ref} {...ownerState} aria-label={t5} {...t6} className={t7} {...other}>{t9}{t10}</ItemRoot>;
    $[35] = navItem.baseProps;
    $[36] = other;
    $[37] = ownerState;
    $[38] = ref;
    $[39] = t10;
    $[40] = t5;
    $[41] = t7;
    $[42] = t9;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  return t11;
}
const shouldForwardProp = prop => !['open', 'active', 'variant', 'sx'].includes(prop);

/** @slot root */
const ItemRoot = styled(ButtonBase, {
  shouldForwardProp
})(({
  active,
  open,
  theme
}) => {
  const dotTransitions = {
    in: {
      opacity: 0,
      scale: 0
    },
    out: {
      opacity: 1,
      scale: 1
    }
  };
  const dotStyles = {
    ...dotTransitions.in,
    width: 6,
    height: 6,
    left: -12,
    content: '""',
    borderRadius: '50%',
    position: 'absolute',
    backgroundColor: varAlpha(theme.vars.palette.text.disabledChannel, 0.64),
    transition: theme.transitions.create(['opacity', 'scale'], {
      duration: theme.transitions.duration.shorter
    }),
    ...(active && {
      ...dotTransitions.out,
      backgroundColor: theme.vars.palette.primary.main
    })
  };
  const rootItemStyles = {
    ...(open && {
      '&::before': {
        ...dotTransitions.out
      }
    }),
    ...(active && {
      color: theme.vars.palette.primary.main
    })
  };
  const subItemStyles = {
    color: theme.vars.palette.text.secondary,
    '&:hover': {
      color: theme.vars.palette.text.primary
    },
    ...(active && {
      color: theme.vars.palette.text.primary
    })
  };
  return {
    transition: theme.transitions.create(['color'], {
      duration: theme.transitions.duration.shorter
    }),
    '&::before': dotStyles,
    '&:hover::before': {
      ...dotTransitions.out
    },
    variants: [{
      props: {
        variant: 'rootItem'
      },
      style: rootItemStyles
    }, {
      props: {
        variant: 'subItem'
      },
      style: subItemStyles
    }]
  };
});

/** @slot title */
const ItemTitle = styled('span', {
  shouldForwardProp
})(({
  theme
}) => ({
  ...navItemStyles.title(theme),
  ...theme.typography.body2,
  fontWeight: theme.typography.fontWeightMedium,
  variants: [{
    props: {
      variant: 'subItem'
    },
    style: {
      fontSize: theme.typography.pxToRem(13)
    }
  }, {
    props: {
      active: true
    },
    style: {
      fontWeight: theme.typography.fontWeightSemiBold
    }
  }]
}));

/** @slot arrow */
const ItemArrow = styled(Iconify, {
  shouldForwardProp
})(({
  theme
}) => ({
  ...navItemStyles.arrow(theme)
}));